import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  CurrencyRupee as RupeeIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { updateAssociateCommission, getAssociateCommissions } from '../../services/associate';
import { getCommissions } from '../../services/master';
import { showSnackbar } from '../../components/Snackbar';
import { hasWritePermission } from '../../utils/menuFilter';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import MoreFilters from '../../components/MoreFilters/MoreFilters';

const DEFAULT_PAGE_SIZE = 10;

const AssociateCommission = () => {
  const canWrite = hasWritePermission('associate.associate-commission');
  const [commissionData, setCommissionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0); // Total records from API
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editCommission, setEditCommission] = useState('');
  const [saving, setSaving] = useState(false);
  const [commissionOptionsFromAPI, setCommissionOptionsFromAPI] = useState([]);

  const [moreFiltersExpanded, setMoreFiltersExpanded] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [tempSortBy, setTempSortBy] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('');
  const [tempFilterDesignation, setTempFilterDesignation] = useState('');
  const [filterSponsorDesignation, setFilterSponsorDesignation] = useState('');
  const [tempFilterSponsorDesignation, setTempFilterSponsorDesignation] = useState('');

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    setFilterDesignation(tempFilterDesignation);
    setFilterSponsorDesignation(tempFilterSponsorDesignation);
    setPage(0);
  };

  const handleResetFilters = () => {
    setSortBy('');
    setTempSortBy('');
    setFilterDesignation('');
    setTempFilterDesignation('');
    setFilterSponsorDesignation('');
    setTempFilterSponsorDesignation('');
    setPage(0);
  };

  const handleFilterKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApplyFilters();
      setMoreFiltersExpanded(false);
    }
  };

  // Build dropdown options for commission based on sponsorCommission and API data
  const sponsorCommissionNumber = editRecord
    ? Number(editRecord.sponsorCommission ?? 0)
    : 0;
  
  // Filter commission options from API based on sponsorCommissionNumber
  const commissionOptions = useMemo(() => {
    if (sponsorCommissionNumber <= 0) {
      return [];
    }
    
    // Filter API commissions that are <= sponsorCommissionNumber
    return commissionOptionsFromAPI
      .filter((item) => {
        const commissionValue = Number(item.commission || item.value || 0);
        return commissionValue > 0 && commissionValue <= sponsorCommissionNumber;
      })
      .map((item) => Number(item.commission || item.value || 0))
      .sort((a, b) => a - b);
  }, [commissionOptionsFromAPI, sponsorCommissionNumber]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  // Debounce search input before hitting API
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);


  const displayData = useMemo(() => commissionData, [commissionData]);

  const handleEdit = (row) => {
    setEditRecord(row);
    setEditCommission(String(row.commission ?? ''));
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editRecord) return;

    const sponsorCommission = Number(editRecord.sponsorCommission ?? 0);
    let value = editCommission === '' ? NaN : Number(editCommission);

    if (Number.isNaN(value)) {
      try {
        showSnackbar({ type: 'error', message: 'Please enter a valid commission value' });
      } catch {
        // ignore snackbar failure
      }
      return;
    }

    if (value < 0) value = 0;
    if (value > sponsorCommission) {
      const msg = "Commission can't be higher than sponsor commission";
      try {
        showSnackbar({ type: 'error', message: msg });
      } catch {
        // ignore snackbar failure
      }
      return;
    }

    try {
      setSaving(true);
      const targetId = editRecord.commissionId || editRecord.id;
      if (!targetId) {
        try {
          showSnackbar({ type: 'error', message: 'Commission ID not available for this record' });
        } catch {
          // ignore snackbar failure
        }
        return;
      }

      const res = await updateAssociateCommission(targetId, value);

      if (res?.status === false) {
        const msg = res?.message || 'Failed to save commission';
        try {
          showSnackbar({ type: 'error', message: msg });
          if (Array.isArray(res.errors)) {
            res.errors.forEach((e) => {
              if (e?.msg) showSnackbar({ type: 'error', message: e.msg });
            });
          }
        } catch {
          // ignore snackbar failure
        }
        return;
      }

      setCommissionData((prev) =>
        prev.map((item) =>
          item.id === editRecord.id ? { ...item, commission: value } : item,
        ),
      );

      try {
        showSnackbar({ type: 'success', message: res?.message || 'Commission updated successfully' });
      } catch {
        // ignore snackbar failure
      }

      setEditDialogOpen(false);
      setEditRecord(null);
      setEditCommission('');
    } catch (err) {
      const msg =
        (err && typeof err === 'object' && ('message' in err || 'error' in err))
          ? (err.message || err.error)
          : 'Failed to save commission';
      try {
        showSnackbar({ type: 'error', message: msg });
        if (err && typeof err === 'object' && Array.isArray(err.errors)) {
          err.errors.forEach((e) => {
            if (e?.msg) showSnackbar({ type: 'error', message: e.msg });
          });
        }
      } catch {
        // ignore snackbar failure
      }
    } finally {
      setSaving(false);
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Fetch commission options from API
  useEffect(() => {
    const fetchCommissions = async () => {
      try {
        const commissions = await getCommissions();
        setCommissionOptionsFromAPI(commissions);
      } catch (error) {
        console.error('Failed to load commission options:', error);
        setCommissionOptionsFromAPI([]);
      }
    };
    fetchCommissions();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Try to infer associateId from logged-in user
      let associateId = '';
      try {
        const userRaw = localStorage.getItem('user');
        if (userRaw) {
          const u = JSON.parse(userRaw);
          associateId = u.associateId || u._id || u.userId || '';
        }
      } catch {}

      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (associateId) params.associateId = associateId;

      if (filterDesignation && filterDesignation.trim()) {
        params.designation = filterDesignation.trim();
      }
      if (filterSponsorDesignation && filterSponsorDesignation.trim()) {
        params.sponsorDesignation = filterSponsorDesignation.trim();
      }

      if (sortBy) {
        let apiSortBy = '';
        let apiSortOrder = 'asc';

        if (sortBy === 'dateDesc') {
          apiSortBy = 'date';
          apiSortOrder = 'desc';
        } else if (sortBy === 'dateAsc') {
          apiSortBy = 'date';
          apiSortOrder = 'asc';
        } else if (sortBy === 'commissionDesc') {
          apiSortBy = 'commission';
          apiSortOrder = 'desc';
        } else if (sortBy === 'commissionAsc') {
          apiSortBy = 'commission';
          apiSortOrder = 'asc';
        } else if (sortBy === 'sponsorCommissionDesc') {
          apiSortBy = 'sponsorCommission';
          apiSortOrder = 'desc';
        } else if (sortBy === 'sponsorCommissionAsc') {
          apiSortBy = 'sponsorCommission';
          apiSortOrder = 'asc';
        }

        if (apiSortBy) {
          params.sortBy = apiSortBy;
          params.sortOrder = apiSortOrder;
        }
      }

      const body = await getAssociateCommissions(params);

      if (body?.status === false) {
        const msg = body?.message || 'Failed to load associate commissions';
        setError(msg);
        try { showSnackbar({ type: 'error', message: msg }); } catch {}
        setCommissionData([]);
        setTotalCount(0);
        return;
      }

      const list = Array.isArray(body?.data) ? body.data : [];
      // Extract total count from API response for pagination (same format as AssociateList)
      const total = body?.extra?.total ? parseInt(body.extra.total, 10) : (body?.totalCount || body?.count || list.length);
      setTotalCount(total);

      const normalized = list.map((item, idx) => {
        const assoc = item.associateId && typeof item.associateId === 'object'
          ? item.associateId
          : null;
        let associateIdValue = '';
        if (typeof item.associateId === 'string') {
          associateIdValue = item.associateId;
        } else if (assoc && assoc._id) {
          associateIdValue = assoc._id;
        }

        return {
          id: item._id || idx,
          commissionId: item._id || '',
          myId: item.myId || (assoc && assoc._id) || '',
          designation: item.designation || (assoc && assoc.designation) || '',
          associateName: item.associateName || (assoc && assoc.name) || '',
          sponsorName: item.sponsorName || '',
          sponsorBy: item.sponsorBy || '',
          sponsorDesignation: item.sponsorDesignation || '',
          sponsorCommission: item.sponsorCommission ?? 0,
          commission: item.commission ?? 0,
          associateId: associateIdValue,
        };
      });

      setCommissionData(normalized);
    } catch (e) {
      const msg = e.message || 'Failed to load associate commissions';
      setError(msg);
      setTotalCount(0);
      try { showSnackbar({ type: 'error', message: msg }); } catch {}
    } finally {
      setLoading(false);
    }
  };

  // Fetch associate commissions from API
  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, debouncedSearch, filterDesignation, filterSponsorDesignation, sortBy]);

  const handleRefresh = () => {
    if (search) {
      setSearch('');
    } else {
      fetchData();
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="primary">
          Associate Commission
        </Typography>
        <RefreshButton onClick={handleRefresh} loading={loading} />
      </Box>

      {/* Search Bar */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={8.5}>
              <TextField
                fullWidth
                placeholder="Search by My ID, Associate Name, Sponsor Name, or Designation"
                value={search}
                onChange={handleSearchChange}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={1.5}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setSearch('');
                  setSortBy('');
                  setTempSortBy('');
                  setFilterDesignation('');
                  setTempFilterDesignation('');
                  setFilterSponsorDesignation('');
                  setTempFilterSponsorDesignation('');
                  setPage(0);
                }}
                fullWidth
                sx={{
                  height: '56px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '12px',
                  borderRadius: 2,
                  borderColor: '#d32f2f',
                  '&:hover': {
                    borderColor: '#b71c1c',
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                  },
                }}
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} sm={2} md={2}>
              <MoreFilters
                expanded={moreFiltersExpanded}
                onToggle={() => setMoreFiltersExpanded(!moreFiltersExpanded)}
                sortBy={tempSortBy}
                onChangeSortBy={setTempSortBy}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
                type="commission"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#536DFE' }}>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>S.No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>My ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Designation</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Associate Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Sponsor Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Sponsor Designation</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Sponsor Commission</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Commission</TableCell>
                {/* {canWrite && <TableCell sx={{ color: 'white', fontWeight: 700 }}>Action</TableCell>} */}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : displayData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    No data found
                  </TableCell>
                </TableRow>
              ) : displayData.map((row, index) => (
                  <TableRow 
                    key={row.id}
                sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{row.myId}</TableCell>
                    <TableCell>{row.designation}</TableCell>
                    <TableCell>{row.associateName}</TableCell>
                    <TableCell>{row.sponsorName}</TableCell>
                    <TableCell>{row.sponsorDesignation}</TableCell>
                    <TableCell>{`${row.sponsorCommission}%`}</TableCell>
                    <TableCell>
                      {`${row.commission}%`}
                    </TableCell>
                    {/* {canWrite && (
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )} */}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
              </Card>

      {/* Pagination */}
      {totalCount > 0 && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, mt: 2 }}>
          <TablePagination
            
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      {/* Add Commission Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#536DFE', color: 'white' }}>
          Add New Commission
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ReceiptIcon sx={{ fontSize: 80, color: '#536DFE', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Add Commission Record
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a new commission record for an associate
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
          >
            Add Commission
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Commission Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditRecord(null);
          setEditCommission('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#536DFE', color: 'white', pb: 1.5 }}>
          Edit Commission
        </DialogTitle>
        <DialogContent sx={{ px: 4, pt: 3, pb: 4, minHeight: 260 }}>
          {editRecord && (
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="My ID"
                  value={editRecord.myId || '-'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Designation"
                  value={editRecord.designation || '-'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Associate Name"
                  value={editRecord.associateName || '-'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Sponsor Name"
                  value={editRecord.sponsorName || '-'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Sponsor Designation"
                  value={editRecord.sponsorDesignation || '-'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Sponsor Commission (%)"
                  value={`${editRecord.sponsorCommission ?? 0}%`}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Commission (%)"
                  select
                  fullWidth
                  value={editCommission}
                  onChange={(e) => setEditCommission(e.target.value)}
                  helperText={
                    sponsorCommissionNumber > 0
                      ? `Select commission between 1 and ${sponsorCommissionNumber}%`
                      : 'Select commission percentage'
                  }
                >
                  {commissionOptions.map((v) => (
                    <MenuItem key={v} value={String(v)}>
                      {v}%
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setEditRecord(null);
              setEditCommission('');
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssociateCommission;
